// Персистер TanStack Query в IndexedDB (таблица `cache`): последние загруженные данные
// переживают перезагрузку и доступны офлайн. Запись не чаще раза в секунду.

import type { QueryClient } from '@tanstack/react-query'
import type { PersistedClient, Persister } from '@tanstack/react-query-persist-client'

import { captureIdentity, getActiveUserId, subscribeIdentity } from '@/api/identity'
import { getOfflineDb } from './db'

const KEY = 'react-query'

export function createIdbPersister({ throttleMs = 1000 }: { throttleMs?: number } = {}): Persister {
  const identity = captureIdentity()
  const db = identity.id ? getOfflineDb() : null
  let pending: PersistedClient | null = null
  let timer: ReturnType<typeof setTimeout> | null = null

  const write = async () => {
    timer = null
    const value = pending
    pending = null
    if (!value || !identity.isCurrent() || !db) return
    try {
      await db.cache.put({ key: KEY, value })
    } catch {
      // нет места или IndexedDB недоступна — кэш не сохранится, приложение работает дальше
    }
  }

  return {
    persistClient(client) {
      if (!identity.isCurrent()) return
      pending = client
      if (timer === null) timer = setTimeout(() => void write(), throttleMs)
    },
    async restoreClient() {
      try {
        if (!db || !identity.isCurrent()) return undefined
        const row = await db.cache.get(KEY)
        if (!identity.isCurrent()) return undefined
        return (row?.value as PersistedClient | undefined) ?? undefined
      } catch {
        return undefined
      }
    },
    async removeClient() {
      pending = null
      if (timer !== null) clearTimeout(timer)
      timer = null
      await db?.cache.delete(KEY)
    },
  }
}

/** Сколько хранить последние данные для офлайна. */
export const CACHE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000
/** Сменить при несовместимом изменении формы ответов — старый кэш выбросится. */
export const CACHE_BUSTER = 'v1'

/**
 * Настройки клиента для офлайна: восстановленные из кэша запросы не выбрасываются через 5 минут
 * (gcTime ≥ maxAge), а формы без связи сразу получают ошибку «нет связи» и не висят в ожидании —
 * записывать офлайн можно только тренировку, через очередь.
 */
export function configureOfflineDefaults(client: QueryClient): void {
  subscribeIdentity((reason) => {
    void client.cancelQueries()
    // An external cookie revision revokes the evidence as well as private data.
    // Retaining fresh auth/state would let the provider remount authenticate A again.
    client.removeQueries({ predicate: (query) => reason === 'revoked' || !(query.queryKey[0] === 'auth' && query.queryKey[1] === 'state') })
    client.getMutationCache().clear()
  })
  const defaults = client.getDefaultOptions()
  client.setDefaultOptions({
    ...defaults,
    queries: { ...defaults.queries, gcTime: CACHE_MAX_AGE_MS,
      queryKeyHashFn: (key) => JSON.stringify(key[0] === 'auth' && key[1] === 'state' ? key : [getActiveUserId(), ...key]),
    },
    mutations: { ...defaults.mutations, networkMode: 'always' },
  })
}
